import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHtmlSafeCore } from "../../../Admin/kReusableCoreTS/kHtmlSafeCore";
import { AppAssistExport } from "./AppAssistExport";
import { IAssistCopied, IAssistMarks, IAssistNode, IAssistTools } from "./AppAssistPanel";

// AppAssistTools - the BROWSER's half of taking a table or chart out of an AI answer (`AppAssistExport`
// holds the rules, `AppAssistPanel` draws): the log's own listeners for tapping a chart mark, the
// clipboard for "Copy table", and the download for "Save chart".
//
// ⭐ ONE SET OF LISTENERS PER LOG ELEMENT, HOWEVER MANY PANELS ARE BUILT OVER IT. The page builds a new
// panel on every contact open; listeners added per panel would pile up and each tap would be answered by
// every panel the page ever had. `Listen` keeps one listener per log (a `WeakMap`, so a log the page threw
// away takes its listener with it) and only moves it to the newest panel. The log's CONTENT is redrawn
// whole, so the listener is delegated - it asks which mark the tap was on.
//
// ⛔ WHAT IS SAVED IS OUR OWN DOM: the chart the panel built from the cleaned tree, cloned, with the
// computed colours written onto it and our own attributes (`tabindex`, the mark numbers) removed, then
// serialised by `XMLSerializer`. No model text is ever parsed here; nothing here sends a message.
// ⚠ A REFUSED CLIPBOARD OR AN ABSENT DOWNLOAD API IS A SENTENCE (fate 1), never the handler: it is the
// browser's answer, and the owner can copy by hand. Only a fault in this code reaches the handler.
// ⚠ Browser only - but nothing here touches a browser global until a method runs, so the Node suite can
// import the panel that defaults to it.

/** The one listener set on one log element; it answers the newest panel built over that log. */
export class AppAssistLogListener {
    public Marks: IAssistMarks;

    public constructor(Marks: IAssistMarks) {
        this.Marks = Marks;
    }

    /** A tap or click: on a mark shows its value, anywhere else in the log hides them all. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const N: number = AppAssistLogListener.MarkOf(Ev);
            if (N >= 0) { this.Marks.ShowMark(N); } else { this.Marks.HideMarks(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistLogListener.OnClick");
        }
    };

    /** Enter or Space on a focused mark shows its value; Escape hides it. */
    public OnKey = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Key: string = nz((Ev as KeyboardEvent).key);
            if (Key === "Escape") {
                this.Marks.HideMarks();
                // ⭐ An open "Remove this answer?" takes the key (its Cancel) - and the same Esc then closes nothing else.
                if (this.Marks.CloseConfirm !== undefined && this.Marks.CloseConfirm() === true) { Ev.stopPropagation(); }
                return;
            }
            if (Key !== "Enter" && Key !== " ") { return; }
            const N: number = AppAssistLogListener.MarkOf(Ev);
            if (N < 0) { return; }
            Ev.preventDefault();
            this.Marks.ShowMark(N);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistLogListener.OnKey");
        }
    };

    /** A mark reached with Tab shows its value - the keyboard's hover. */
    public OnFocus = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const N: number = AppAssistLogListener.MarkOf(Ev);
            if (N >= 0) { this.Marks.ShowMark(N); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistLogListener.OnFocus");
        }
    };

    /** The mark number the event happened on (itself or an ancestor carries `data-wac-mark`), else -1. */
    public static MarkOf = (Ev: Event): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            const Target: unknown = Ev.target;
            if (Target === null || typeof Target !== "object") { return -1; }
            const Closest: unknown = (Target as Record<string, unknown>).closest;
            if (typeof Closest !== "function") { return -1; }
            const Mark: Element | null = (Target as Element).closest("[" + AppAssistExport.MarkAttr + "]");
            if (Mark === null) { return -1; }
            const N: number = parseInt(nz(Mark.getAttribute(AppAssistExport.MarkAttr)), 10);
            if (isNaN(N) === true || N < 0) { return -1; }
            return N;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistLogListener.MarkOf");
            return -1;
        }
    };
}

/** One "Copy table": the Clipboard API first, then the old `execCommand("copy")`, answered once. */
export class AppAssistCopyJob {
    private Text: string;
    private Then: IAssistCopied;
    private Ended: boolean = false;

    public constructor(Text: string, Then: IAssistCopied, private AllowFallback: boolean = true,
                       private StartLabel: string = "AppAssistCopyJob.Start") {
        this.Text = Text;
        this.Then = Then;
    }

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Clip: Clipboard | undefined = navigator.clipboard as Clipboard | undefined;
            if (Clip === undefined || Clip === null) { this.OnRefused(); return; }
            if (this.AllowFallback === true && typeof Clip.writeText !== "function") { this.Fallback(); return; }
            Clip.writeText(this.Text).then(this.OnCopied).catch(this.OnRefused);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, this.StartLabel);
        }
    };

    private OnCopied = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.Then.Copied();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistCopyJob.OnCopied");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: a refused `writeText` is a permission answer - the fallback is tried next. */
    private OnRefused = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            if (this.AllowFallback === false) { this.Ended = true; this.Then.Refused(); return; }
            this.Fallback();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistCopyJob.OnRefused");
        }
    };

    /** A hidden textarea, selected and copied the old way; its answer decides Copied or Refused. */
    private Fallback = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            const Box: HTMLTextAreaElement = document.createElement("textarea");
            Box.value = this.Text;
            Box.setAttribute("readonly", "");
            Box.setAttribute("aria-hidden", "true");
            Box.style.position = "fixed";
            Box.style.top = "-1000px";
            Box.style.opacity = "0";
            document.body.appendChild(Box);
            Box.select();
            const Ok: boolean = AppAssistCopyJob.ExecCopy();
            document.body.removeChild(Box);
            if (Ok === true) { this.Then.Copied(); } else { this.Then.Refused(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistCopyJob.Fallback");
        }
    };

    /**
     * `document.execCommand("copy")` - false when the browser refuses. ERROR-SUPPRESSED-JUSTIFIED: some
     * browsers THROW instead of answering false (no user gesture, command disabled); either way it is the
     * browser's answer, shown as "Couldn't copy …", never a fault of ours.
     */
    private static ExecCopy = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (typeof document.execCommand !== "function") { return false; }
            return document.execCommand("copy") === true;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: see the method's comment - a refusal, shown to the owner as one.
            return false;
        }
    };
}

/** The object URL of one saved chart, revoked a while after the download started. */
export class AppAssistUrlDrop {
    private Url: string;

    public constructor(Url: string) {
        this.Url = Url;
    }

    public OnTimer = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            URL.revokeObjectURL(this.Url);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistUrlDrop.OnTimer");
        }
    };
}

export class AppAssistTools implements IAssistTools {
    /** One listener per log element. ⚠ Weak: a log the page replaced is not kept alive by it. */
    private static Listeners: WeakMap<object, AppAssistLogListener> = new WeakMap<object, AppAssistLogListener>();
    /** How long a saved chart's object URL lives - long enough for any browser to start the download. */
    public static readonly RevokeMs: number = 60000;

    /** `IAssistTools.Listen`. ⚠ A log that cannot listen (the suite's fakes) is simply not listened to. */
    public Listen = (Log: IAssistNode, Marks: IAssistMarks): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Record<string, unknown> = Log as unknown as Record<string, unknown>;
            if (typeof Target.addEventListener !== "function") { return; }
            const Known: AppAssistLogListener | undefined = AppAssistTools.Listeners.get(Log);
            if (Known !== undefined) { Known.Marks = Marks; return; }
            const Fresh: AppAssistLogListener = new AppAssistLogListener(Marks);
            const El: EventTarget = Log as unknown as EventTarget;
            El.addEventListener("click", Fresh.OnClick);
            El.addEventListener("keydown", Fresh.OnKey);
            El.addEventListener("focusin", Fresh.OnFocus);
            AppAssistTools.Listeners.set(Log, Fresh);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTools.Listen");
        }
    };

    /** `IAssistTools.Copy`. */
    public Copy = (Text: string, Then: IAssistCopied): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            new AppAssistCopyJob(nz(Text), Then).Start();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTools.Copy");
        }
    };

    /**
     * `IAssistTools.Save`: the built chart as a standalone .svg - a CLONE with the computed colours written on,
     * our own attributes removed, the panel's background behind it, a size - downloaded through a Blob URL.
     */
    public Save = (Chart: IAssistNode, Name: string): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistExport.SaveRefusedNote(); }
        try {
            if (typeof XMLSerializer === "undefined" || typeof Blob === "undefined" || typeof URL === "undefined"
                || typeof URL.createObjectURL !== "function") { return AppAssistExport.SaveRefusedNote(); }
            const Text: string = AppAssistTools.FileText(Chart as unknown as SVGSVGElement);
            if (Text.length === 0) { return AppAssistExport.SaveRefusedNote(); }
            const Url: string = URL.createObjectURL(new Blob([Text], { type: "image/svg+xml;charset=utf-8" }));
            const Link: HTMLAnchorElement = document.createElement("a");
            Link.href = Url;
            Link.download = nz(Name);
            Link.rel = "noopener";
            Link.style.display = "none";
            document.body.appendChild(Link);
            Link.click();
            document.body.removeChild(Link);
            setTimeout(new AppAssistUrlDrop(Url).OnTimer, AppAssistTools.RevokeMs);
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTools.Save");
            return AppAssistExport.SaveRefusedNote();
        }
    };

    /** The file's text: an XML declaration and the serialised clone. */
    public static FileText = (Svg: SVGSVGElement): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Copy: SVGSVGElement = Svg.cloneNode(true) as SVGSVGElement;
            AppAssistTools.Inline(Svg, Copy);
            AppAssistTools.Unmark(Copy);
            Copy.setAttributeNS("http://www.w3.org/2000/xmlns/", "xmlns", kHtmlSafeCore.SvgNs);
            const Box: DOMRect = Svg.getBoundingClientRect();
            if (Copy.getAttribute("width") === null && Box.width > 0) { Copy.setAttribute("width", String(Math.round(Box.width))); }
            if (Copy.getAttribute("height") === null && Box.height > 0) { Copy.setAttribute("height", String(Math.round(Box.height))); }
            AppAssistTools.Backdrop(Svg, Copy);
            return "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n" + new XMLSerializer().serializeToString(Copy);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTools.FileText");
            return "";
        }
    };

    /**
     * Each element's computed paint and font (`AppAssistExport.PaintProps`) onto its clone, as `style` - the
     * palette classes and `currentColor` live in the app's stylesheet, which the file does not carry.
     * ⚠ The clone and the original are walked together: `querySelectorAll("*")` is document order on both.
     */
    private static Inline = (From: SVGSVGElement, To: SVGSVGElement): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Olds: Element[] = [From as Element];
            const News: Element[] = [To as Element];
            for (const One of Array.from(From.querySelectorAll("*"))) { Olds.push(One); }
            for (const One of Array.from(To.querySelectorAll("*"))) { News.push(One); }
            for (let At: number = 0; At < Olds.length && At < News.length; At = At + 1) {
                const Seen: CSSStyleDeclaration = window.getComputedStyle(Olds[At]);
                const Values: string[] = [];
                for (const Prop of AppAssistExport.PaintProps) { Values.push(Seen.getPropertyValue(Prop)); }
                const Style: string = AppAssistExport.StyleOf(AppAssistExport.PaintProps, Values);
                // ⛔ THROUGH THE CSSOM: `setAttribute` of a style is refused by the app's CSP (no 'unsafe-inline'),
                // even on a detached clone; `cssText` is not, and serialises as the same attribute.
                if (Style.length > 0) { (News[At] as SVGElement).style.cssText = Style; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTools.Inline");
        }
    };

    /** Our own attributes off the clone: the mark numbers, the highlight, the tab stops. */
    private static Unmark = (Copy: SVGSVGElement): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const One of Array.from(Copy.querySelectorAll("[" + AppAssistExport.MarkAttr + "]"))) {
                One.removeAttribute(AppAssistExport.MarkAttr);
                One.removeAttribute(AppAssistExport.OnAttr);
                One.removeAttribute("tabindex");
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTools.Unmark");
        }
    };

    /**
     * The panel's background as the first shape, so ink text (light in dark mode) stays readable in any
     * viewer. Taken from the nearest ancestor that paints one; none found = no backdrop.
     */
    private static Backdrop = (From: SVGSVGElement, To: SVGSVGElement): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let Colour: string = "";
            let Up: Element | null = From.parentElement;
            while (Up !== null && Colour.length === 0) {
                const Seen: string = window.getComputedStyle(Up).backgroundColor;
                if (AppAssistExport.IsClear(Seen) !== true) { Colour = Seen; }
                Up = Up.parentElement;
            }
            if (Colour.length === 0) { return; }
            const Box: string[] = AppAssistExport.BackdropOf(nz(To.getAttribute("viewBox")));
            const Rect: Element = document.createElementNS(kHtmlSafeCore.SvgNs, "rect");
            Rect.setAttribute("x", Box[0]);
            Rect.setAttribute("y", Box[1]);
            Rect.setAttribute("width", Box[2]);
            Rect.setAttribute("height", Box[3]);
            Rect.setAttribute("fill", Colour);
            To.insertBefore(Rect, To.firstChild);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistTools.Backdrop");
        }
    };
}
